import { RegPlateInput } from '@/components/ui/RegPlateInput/RegPlateInput';
import { Select } from '@/components/ui/Select/Select';
import { eachYearOfInterval } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { InView } from 'react-intersection-observer';
import styles from './CarSelectionForm.module.css';

export type VehicleType = 'privatecar' | 'commercialvehicle';

const PRIVATE_CAR = 'privatecar';
const COMMERCIAL_VEHICLE = 'commercialvehicle';

const LOADING = 'loading';
const ERROR = 'error';
const DONE = 'done';
type FetchingState = 'loading' | 'done' | 'error';

const generateYearsOptions = (startYear: number) => {
  const today = new Date();
  const january1920 = new Date(startYear, 0, 1);
  const years = eachYearOfInterval({ start: january1920, end: today })
    .map((date) => {
      const year = date.getFullYear().toString();
      return {
        label: year,
        value: year,
      };
    })
    .reverse();

  return years;
};

export const CarSelectionForm: React.FC<{
  className: string;
  regLabel: string;
  showRegLabel?: boolean;
  showRegToggle?: boolean;
  journey?: string;
  reg: string;
  type: VehicleType | '';
  setReg: (reg: string) => void;
  setType: (type: VehicleType | '') => void;
}> = ({ className, regLabel, showRegLabel = true, showRegToggle = true, journey, reg, type, setReg, setType }) => {
  const [knowsTheirReg, setKnowsTheirReg] = useState(true);
  const [vehicleWeight, setVehicleWeight] = useState<number | null>(null);
  const [vehicleMakes, setVehicleMakes] = useState<string[]>([]);

  const [, setManufacturingYear] = useState<string>('');
  const [makeFetchingState, setMakeFetchState] = useState<FetchingState>(DONE);
  const [modelsFetchingState, setModelsFetchState] = useState<FetchingState>(DONE);
  const [variantsFetchingState, setVariantsFetchState] = useState<FetchingState>(DONE);

  const [make, setMake] = useState('');
  const [models, setModels] = useState<string[]>([]);
  const [model, setModel] = useState('');
  const [, setYear] = useState<string>('');

  const [variant, setVariant] = useState('');
  const [variants, setVariants] = useState<{ text: string; value: string }[]>([]);
  const regInputRef = useRef<HTMLInputElement | null>(null);
  const firstRender = useRef(true);

  const years = generateYearsOptions(1920);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (regInputRef.current && knowsTheirReg === true) {
      regInputRef.current.focus();
    }
  }, [knowsTheirReg, journey]);

  useEffect(() => {
    if (!type) return;
    setMakeFetchState(LOADING);
    fetch(`/builder-home-rebrand/api/vehicles/${type}/make`)
      .then((res) => res.json())
      .then((jsonResponse) => {
        if (!jsonResponse.makes) {
          setMakeFetchState(ERROR);
          return;
        }

        setVehicleMakes(jsonResponse.makes);
        setMakeFetchState(DONE);
      })
      .catch(() => {
        setMakeFetchState(ERROR);
      });
  }, [type]);

  useEffect(() => {
    if (!make) return;
    setModelsFetchState(LOADING);
    fetch(`/builder-home-rebrand/api/vehicles/${type}/makes/${make}/models`)
      .then((res) => res.json())
      .then((jsonResponse) => {
        if (!jsonResponse.models) {
          setModelsFetchState(ERROR);
          return;
        }

        setModels(jsonResponse.models);
        setModelsFetchState(DONE);
      })
      .catch(() => {
        setModelsFetchState(ERROR);
      });
  }, [make, type]);

  useEffect(() => {
    if (!model) return;
    setVariantsFetchState(LOADING);

    fetch(`/builder-home-rebrand/api/vehicles/${type}/makes/${make}/models/${model}/variants`)
      .then((res) => res.json())
      .then((jsonResponse) => {
        if (!jsonResponse.variants) {
          setVariantsFetchState(ERROR);
          return;
        }

        setVariants(jsonResponse.variants);
        setVariantsFetchState(DONE);
      })
      .catch(() => {
        setVariants([]);
        setVariantsFetchState(ERROR);
      });
  }, [type, make, model]);

  return (
    <InView
      as="div"
      className={className}
      onChange={(inView) => {
        if (!inView) return;

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'amplitude_event',
          event_name: 'registration_form_displayed',
          event_properties: {
            product_type: type,
            registration: reg,
          },
        });
      }}
    >
      {knowsTheirReg ? (
        <RegPlateInput
          id="reg"
          ref={regInputRef}
          name="registration"
          value={reg.toUpperCase().replace(' ', '')}
          onChange={(e) => {
            setReg(e.target.value);
          }}
          placeholder=" "
          required
          pattern="(^[A-Z]{2}[0-9]{2}\s?[A-Z]{3}$)|(^[A-Z][0-9]{1,3}[A-Z]{3}$)|(^[A-Z]{3}[0-9]{1,3}[A-Z]$)|(^[0-9]{1,4}[A-Z]{1,2}$)|(^[0-9]{1,3}[A-Z]{1,3}$)|(^[A-Z]{1,2}[0-9]{1,4}$)|(^[A-Z]{1,3}[0-9]{1,3}$)|(^[A-Z]{1,3}[0-9]{1,4}$)|(^[0-9]{3}[DX]{1}[0-9]{3}$)"
          minLength={2}
          maxLength={8}
          validationError="Please enter a valid registration"
          label={regLabel}
          showLabel={showRegLabel}
        >
          {showRegToggle && (
            <button
              type="button"
              onClick={() => {
                setKnowsTheirReg(!knowsTheirReg);
                setType('');
                setVehicleWeight(null);
                setMake('');
                setModel('');
                setVariant('');
              }}
              aria-label="Enables you to select the type of vehicle, make, weight, model and variant"
              className="hover:text-secondary pl-px text-sm font-bold underline"
            >
              I don&lsquo;t know my reg yet
            </button>
          )}
        </RegPlateInput>
      ) : (
        <div className={`flex flex-col ${styles.fieldContainer}`}>
          <label className="tc-label mb-1 pl-px" htmlFor="vehicleType">
            Find your vehicle from the options below
          </label>
          <Select
            id="vehicleType"
            name="type"
            required
            onChange={(e) => {
              setType(e.target.value as VehicleType);
              setVehicleWeight(null);
              setMake('');
              setModel('');
              setVariant('');
            }}
            defaultValue={undefined}
            className={`tc-field mb-2 w-full ${styles.vehicleTypeField}`}
          >
            <option value="">What type of vehicle would you like to insure?</option>
            <option value={PRIVATE_CAR}>Car</option>
            <option value={COMMERCIAL_VEHICLE}>Commercial Vehicle</option>
          </Select>

          {type === COMMERCIAL_VEHICLE && (
            <input
              name="weight"
              value={String(vehicleWeight)}
              required
              type="number"
              min={1}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                setVehicleWeight(value);
                setManufacturingYear('');
                setMake('');
                setModel('');
                setVariant('');
              }}
              className={`tc-field mb-2 w-full ${styles.weightField} border-dark-200 h-11 flex-auto rounded border-1 pl-2`}
              placeholder="Weight in kgs"
            />
          )}
          {vehicleWeight !== null && vehicleWeight > 3500 && (
            <Select
              id="manufacturingYear"
              required
              name="year"
              onChange={(e) => {
                setManufacturingYear(e.target.value);
                setMake('');
                setModel('');
                setVariant('');
              }}
              className={`tc-field mb-2 w-full ${styles.manufacturingYearField}`}
            >
              <option value="">Select year of manufacture</option>
              {Array.from(Array(new Date().getFullYear() - 1919)).map((year, idx) => (
                <option key={year} value={year}>
                  {new Date().getFullYear() - idx}
                </option>
              ))}
            </Select>
          )}
          {type === PRIVATE_CAR && (
            <Select
              required
              name="make"
              disabled={makeFetchingState === LOADING}
              showLoadingSpinner={makeFetchingState === LOADING}
              onChange={(e) => {
                setMake(e.target.value);
                setModel('');
                setVariant('');
              }}
              className={`tc-field mb-2 w-full ${styles.makeField} ${makeFetchingState === ERROR ? 'border-error' : ''}`}
            >
              <option value="">Make</option>
              {vehicleMakes.map((make) => (
                <option key={make} value={make}>
                  {make}
                </option>
              ))}
            </Select>
          )}
          {type === COMMERCIAL_VEHICLE && vehicleWeight !== null && vehicleWeight <= 3500 && (
            <Select
              required
              name="make"
              disabled={makeFetchingState === LOADING}
              showLoadingSpinner={makeFetchingState === LOADING}
              onChange={(e) => {
                setMake(e.target.value);
                setModel('');
                setVariant('');
              }}
              className={`tc-field mb-2 w-full ${styles.makeField} ${makeFetchingState === ERROR ? 'border-error' : ''}`}
            >
              <option value="">Make</option>
              {vehicleMakes.map((make) => (
                <option key={make} value={make}>
                  {make}
                </option>
              ))}
            </Select>
          )}
          {make !== '' && (
            <Select
              required
              name="model"
              disabled={modelsFetchingState === LOADING}
              showLoadingSpinner={modelsFetchingState === LOADING}
              onChange={(e) => {
                setModel(e.target.value);
                setVariant('');
              }}
              className={`tc-field mb-2 w-full ${styles.modelField} ${modelsFetchingState === ERROR ? 'border-error' : ''}`}
            >
              <option value="">Model</option>
              {models.map((model) => (
                <option key={model} value={model}>
                  {model}
                </option>
              ))}
            </Select>
          )}
          {model !== '' && (
            <Select
              required
              name="variant"
              disabled={variantsFetchingState === LOADING}
              showLoadingSpinner={variantsFetchingState === LOADING}
              onChange={(e) => {
                setVariant(e.target.value);
              }}
              className={`tc-field mb-2 w-full ${styles.variantField} ${variantsFetchingState === ERROR ? 'border-error' : ''}`}
            >
              <option value="">Variant</option>
              {variants.map(({ text, value }) => (
                <option key={value} value={value}>
                  {text}
                </option>
              ))}
            </Select>
          )}
          {variant !== '' && type === COMMERCIAL_VEHICLE && (
            <Select
              required
              name="year"
              onChange={(e) => {
                setYear(e.target.value);
              }}
              className={`tc-field mb-2 w-full ${styles.yearField}`}
            >
              <option value="">Select year</option>
              {years.map(({ label, value }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          )}

          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setKnowsTheirReg(!knowsTheirReg);
              setReg('');
              setType('');
              setVehicleWeight(null);
              setMake('');
              setModel('');
              setVariant('');
              setYear('');
            }}
            className="hover:text-secondary block w-full pl-px text-sm font-bold underline"
            aria-label="Enables you to fill in your registration plate"
          >
            Just kidding. I <em>do</em> know the reg really
          </button>
          <div className={`${styles.validationErrorContainer} mt-1 mb-1 min-h-5`}>
            {[
              { style: styles.validationErrorVehicleType, message: 'Please select the vehicle type' },
              { style: styles.validationErrorWeight, message: 'Please fill in the weight of your vehicle' },
              { style: styles.validationErrorManufacture, message: 'Please select the year of manufacture' },
              { style: styles.validationErrorMake, message: 'Please select a make' },
              { style: styles.validationErrorModel, message: 'Please select a model' },
              { style: styles.validationErrorVariant, message: 'Please select a variant' },
              { style: styles.validationErrorYear, message: 'Please select a year' },
            ].map(({ style, message }, idx) => (
              <p key={idx} className={`${style} text-error m-0 h-0 text-center text-sm`} aria-live="assertive">
                {message}
              </p>
            ))}
          </div>
        </div>
      )}
    </InView>
  );
};
